import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import styled from 'styled-components';

const StyledModal = styled(Modal)`
  .modal-content {
    border-radius: 12px;
    border: none;
  }
`;

const StyledModalHeader = styled(ModalHeader)`
  background: white;
  color: #7c3aed;
  border-bottom: none;
  padding: 24px 32px 16px 32px;

  .modal-title {
    font-size: 20px;
    font-weight: 600;
    color: #7c3aed;
  }

  .close {
    color: #9ca3af;
    opacity: 1;
    text-shadow: none;
    font-size: 28px;
    font-weight: 300;
    padding: 0;
    margin: 0;

    &:hover {
      color: #6b7280;
    }
  }
`;

const ConfirmationText = styled.div`
  font-size: 15px;
  color: #374151;
  margin-bottom: 20px;
  font-weight: 500;
`;

const StyledModalBody = styled(ModalBody)`
  padding: 16px 32px 24px 32px;
`;

const StyledFormGroup = styled(FormGroup)`
  margin-bottom: 16px;
  display: flex;
  align-items: center;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledLabel = styled(Label)`
  font-weight: 600;
  color: #374151;
  font-size: 15px;
  margin-bottom: 0;
  margin-right: 16px;
  min-width: 120px;
  text-align: left;
`;

const StyledInput = styled(Input)`
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  color: #6b7280;
  flex: 1;

  &:focus {
    background-color: white;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
    outline: none;
  }

  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
`;

const StyledSelect = styled(Input)`
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  color: #374151;
  flex: 1;
  appearance: none;
  /* 🔥 Important for ellipsis */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
  cursor: pointer;

  &:focus {
    background-color: white;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
    outline: none;
  }

  &.with-icon {
    background-image:
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E"),
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E");
    background-position:
      right 12px center,
      right 36px center;
    padding-right: 60px;
  }

  &.with-time-icon {
    background-image:
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E"),
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Ccircle cx='12' cy='12' r='10'%3E%3C/circle%3E%3Cpolyline points='12 6 12 12 16 14'%3E%3C/polyline%3E%3C/svg%3E");
    background-position:
      right 12px center,
      right 36px center;
    padding-right: 60px;
  }
`;

const StyledModalFooter = styled(ModalFooter)`
  padding: 20px 32px 28px 32px;
  display: flex;
  justify-content: center;
  gap: 12px;
  border-top: none;
`;

const StyledButton = styled(Button)`
  border-radius: 8px;
  padding: 12px 48px;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.2s;
  min-width: 140px;

  &.btn-primary {
    background: linear-gradient(135deg, #7c3aed 0%, #6366f1 100%);
    border: none;
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(124, 58, 237, 0.4);
      background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.btn-secondary {
    background: white;
    color: #7c3aed;
    border: 2px solid #e5e7eb;

    &:hover {
      background: #f9fafb;
      color: #6d28d9;
      border-color: #d1d5db;
    }
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 16px;
  color: #7c3aed;
  font-size: 14px;
  font-weight: 500;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const InfoBox = styled.div`
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #4c1d95;
  line-height: 1.5;
`;

// sample centresAndDoctorsData
/*
const  centresAndDoctorsData1 = [
  {
    "centreId": "1",
    "centreName": "MyDental Plan",
    "doctors": [
      { "doctorPracticeId": "101", "doctorName": "Dr. Sourav Verma", "doctorOpdCharge":100 },
      { "doctorPracticeId": "102", "doctorName": "Dr. Anu Joseph" ,"doctorOpdCharge":200}
    ]
  },
  {
    "centreId": "2",
    "centreName": "Manya Sector 70",
    "doctors": [
      { "doctorPracticeId": "201", "doctorName": "Dr. Rakesh Kumar","doctorOpdCharge":1000 },
      { "doctorPracticeId": "202", "doctorName": "Dr. Priya Singh","doctorOpdCharge":1000 }
    ]
  }
]
*/
// sample timeslot api data
/*
const data = [
  {
    "date": "2026-02-09",
    "slots": [
      { "slotId": 2, "slot": "08:00 AM" },
      { "slotId": 4, "slot": "02:00 PM" },
      { "slotId": 5, "slot": "05:00 PM" }
    ]
  },
  {
    "date": "2026-02-10",
    "slots": [
      { "slotId": 2, "slot": "08:00 AM" },
      { "slotId": 4, "slot": "02:00 PM" }
    ]
  }
]
 */

// doctorOpdCharge

const ChangeCentreModal = ({
  modalIsOpen,
  closeModal,
  submitModal,
  appointmentData,
  onComplete,
  centresAndDoctorsData,
  availableSlotsData,
  isLoading = false,
  reasonOptions = [
    'Doctor not available',
    'Patient requested',
    'Centre not available',
    'Other',
  ],
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedCentreId, setSelectedCentreId] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [customReason, setCustomReason] = useState('');

  const formatDateForDisplay = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const findNearestDate = (dates) => {
    if (!dates?.length) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      dates.find((d) => {
        const date = new Date(d.date);
        date.setHours(0, 0, 0, 0);
        return date >= today;
      }) || dates[0]
    );
  };

  useEffect(() => {
    if (!modalIsOpen) {
      setSelectedReason('');
      setCustomReason('');
      setSelectedCentreId('');
      setSelectedDoctorId('');
      setAvailableDoctors([]);
      setSelectedDate('');
      setSelectedSlotId('');
      setAvailableSlots([]);
      return;
    }

    setSelectedReason(reasonOptions[0] || '');

    // Try to match the current appointment's center, fall back to first
    const matchedCentre =
      centresAndDoctorsData.find(
        (c) => Number(c.centerId) === Number(appointmentData?.centerId)
      ) || centresAndDoctorsData[0];

    if (matchedCentre) {
      setSelectedCentreId(matchedCentre.centerId);
      const doctors = matchedCentre.doctors || [];
      setAvailableDoctors(doctors);

      // Try to match by doctorPracticeId, then by doctor name, then first valid
      const matchedDoctor =
        doctors.find(
          (d) => appointmentData?.doctorPracticeId &&
            Number(d.doctorPracticeId) === Number(appointmentData?.doctorPracticeId)
        ) ||
        doctors.find(
          (d) => appointmentData?.doctor &&
            d.doctorName?.trim().toLowerCase() === appointmentData.doctor.trim().toLowerCase()
        );
      const fallbackDoctor = doctors.find(
        (d) => Number(d.doctorOpdCharge) <= Number(appointmentData?.amount)
      );
      setSelectedDoctorId(
        matchedDoctor?.doctorPracticeId ||
        fallbackDoctor?.doctorPracticeId ||
        doctors[0]?.doctorPracticeId ||
        ''
      );
    }

    const nearestDate = findNearestDate(availableSlotsData);
    if (nearestDate) {
      setSelectedDate(nearestDate.date);
      setAvailableSlots(nearestDate.slots || []);
      setSelectedSlotId(nearestDate.slots?.[0]?.slotId || '');
    }
  }, [modalIsOpen]);


  const handleCentreChange = (e) => {
    const centerId = e.target.value;
    setSelectedCentreId(centerId);

    const centre = centresAndDoctorsData.find(
      (c) => Number(c?.centerId) === Number(centerId)
    );

    const doctors = centre?.doctors || [];

    setAvailableDoctors(doctors);
    const validDoctor = doctors.find(
      (d) => Number(d?.doctorOpdCharge) <= Number(appointmentData?.amount)
    );

    setSelectedDoctorId(validDoctor?.doctorPracticeId || '');
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);

    const dateObj = availableSlotsData?.find((d) => d.date === date);
    setAvailableSlots(dateObj?.slots || []);
    setSelectedSlotId(dateObj?.slots?.[0]?.slotId || '');
  };

  const handleConfirm = () => {
    if (!selectedReason) return toast.error('Please select a reason');

    if (selectedReason === 'Other' && !customReason.trim()) {
      return toast.error('Please enter the custom reason');
    }
    if (!selectedCentreId) return toast.error('Please select a centre');
    if (!selectedDoctorId) return toast.error('Please select a doctor');
    if (!selectedDate) return toast.error('Please select a date');
    if (!selectedSlotId) return toast.error('Please select a time slot');

    const selectedDoctorObj = availableDoctors.find(
      (d) => Number(d?.doctorPracticeId) === Number(selectedDoctorId)
    );

    if (
      selectedDoctorObj &&
      Number(selectedDoctorObj?.doctorOpdCharge) > appointmentData?.amount
    ) {
      return toast.error(
        'Cannot select doctor with higher consultation price than current.'
      );
    }

    const changeCentreData = {
      requestId: appointmentData ? appointmentData.rid : undefined,
      reason: selectedReason === 'Other' ? customReason.trim() : selectedReason,
      centerId: selectedCentreId,
      doctorPracticeId: selectedDoctorId,
      date: selectedDate,
      slotId: Number(selectedSlotId),
      partnerId: Number(appointmentData?.partnerId) || null,
      doctorOpdCharge: Number(appointmentData?.doctorOpdCharge) || null,
      // amount is total amount paid by user when booking consulatation, taken from cashless_Dental_request_transactions table
      amount: Number(appointmentData?.amount) || null,
    };

    console.log('appointmentData', appointmentData);

    if (submitModal) {
      submitModal(changeCentreData);
    }

    if (onComplete) {
      onComplete();
    }

    closeModal();
  };

  return (
    <StyledModal isOpen={modalIsOpen} toggle={closeModal}>
      <StyledModalHeader toggle={closeModal}>Change Centre</StyledModalHeader>

      <StyledModalBody>
        {isLoading ? (
          <LoadingOverlay>
            <Spinner />
            Loading centre and slot data...
          </LoadingOverlay>
        ) : (
        <>
        <ConfirmationText>Are you sure to change centre?</ConfirmationText>
        <InfoBox>
          <strong>Amount Paid By User:</strong> ₹{appointmentData?.amount}
          <br />
          Centre and doctor can only be changed to options where the
          consultation price is less than or equal to the amount paid.
          <br />
          Doctors with higher consultation fees are disabled.
        </InfoBox>

        <StyledFormGroup>
          <StyledLabel>RID</StyledLabel>
          <StyledInput disabled value={appointmentData?.rid || ''} />
        </StyledFormGroup>


        <StyledFormGroup>
          <StyledLabel>Reason</StyledLabel>
          <StyledSelect
            type="select"
            value={selectedReason}
            onChange={(e) => {
              setSelectedReason(e.target.value);
              if (e.target.value !== 'Other') {
                setCustomReason('');
              }
            }}
          >
            {reasonOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </StyledSelect>
        </StyledFormGroup>

        {selectedReason === 'Other' && (
          <StyledFormGroup>
            <StyledLabel>Specify Reason</StyledLabel>
            <StyledInput
              type="text"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Enter custom reason"
              maxLength={150}
            />
          </StyledFormGroup>
        )}

        <StyledFormGroup>
          <StyledLabel>Centre</StyledLabel>
          <StyledSelect
            type="select"
            value={selectedCentreId}
            onChange={handleCentreChange}
          >
            {centresAndDoctorsData?.map((c, index) => {
              const fullText = `${c?.centerName} — ${c?.centerAddress}`;
              const maxLength = 60;

              const displayText =
                fullText.length > maxLength
                  ? `${fullText.slice(0, maxLength)}...`
                  : fullText;

              return (
                <option
                  key={c?.centerId || `centre-${index}`}
                  value={c?.centerId}
                  title={fullText}
                >
                  {displayText}
                </option>
              );
            })}
          </StyledSelect>
        </StyledFormGroup>

        <StyledFormGroup>
          <StyledLabel>Doctor</StyledLabel>
          <StyledSelect
            type="select"
            value={selectedDoctorId}
            onChange={(e) => setSelectedDoctorId(e?.target?.value)}
          >
            {availableDoctors?.map((d, index) => {
              const consultationPrice = Number(d?.doctorOpdCharge || 0);
              const isDisabled =
                consultationPrice > Number(appointmentData?.amount || 0);

              return (
                <option
                  key={d?.doctorPracticeId || `doctor-${index}`}
                  value={d?.doctorPracticeId}
                  disabled={isDisabled}
                >
                  {d?.doctorName} — ₹{consultationPrice}
                  {isDisabled
                    ? ' (Higher than current consultation price)'
                    : ''}
                </option>
              );
            })}
          </StyledSelect>
        </StyledFormGroup>

        <StyledFormGroup>
          <StyledLabel>Date</StyledLabel>
          <StyledSelect
            type="select"
            value={selectedDate}
            onChange={handleDateChange}
          >
            {availableSlotsData?.map((d) => (
              <option key={d?.date} value={d?.date}>
                {formatDateForDisplay(d?.date)}
              </option>
            ))}
          </StyledSelect>
        </StyledFormGroup>

        <StyledFormGroup>
          <StyledLabel>Timing</StyledLabel>
          <StyledSelect
            type="select"
            value={selectedSlotId}
            onChange={(e) => setSelectedSlotId(e.target.value)}
          >
            {availableSlots?.map((s) => (
              <option key={s?.slotId} value={s?.slotId}>
                {s?.slot}
              </option>
            ))}
          </StyledSelect>
        </StyledFormGroup>
        </>
        )}
      </StyledModalBody>

      <StyledModalFooter>
        <StyledButton color="primary" onClick={handleConfirm} disabled={isLoading}>
          Confirm
        </StyledButton>
        <StyledButton color="secondary" onClick={closeModal}>
          Cancel
        </StyledButton>
      </StyledModalFooter>
    </StyledModal>
  );
};

export default ChangeCentreModal;